// @TODO We're using dangerouslySetInnerHTML in this component
// which should be removed once the backend is able to output
// plain text instead of html
import React, { useState, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled, { ThemeContext } from 'styled-components/macro'
import ExpandableRow from 'components/molecules/expandable-row'
import Checkbox from 'components/atoms/checkbox'
import Text from 'components/atoms/text'
import Icon from 'components/atoms/icon'
import { formatDataTestE2eAttr } from 'utilities/format'
import Button from 'components/atoms/button'
import FlexibleDialog from 'components/molecules/flexible-dialog'
import GpsrDialogForm from './gpsr-dialog-form'
import { useMutation } from '@apollo/client'
import { INSERT_PARTY } from 'config/graphql/v4'
import { withApolloV4Provider } from 'utilities/apollo'
import { getCarfile } from 'redux/actions/data'
import { GPSR_PORTAL_NAMES } from 'config/consts'
import { useMediaLayout } from 'use-media'
import { GpsrPartyDataDialogValidationSchema } from 'config/validation-schemas'

const Date = styled(Text)`
  margin-left: ${({ theme }) => theme.sizings.lvl1};
`

const StyledGpsrButton = styled(Button)`
  padding: 0;
`

function Portal({
  date,
  log,
  onChange,
  selected,
  status,
  title,
  disabled,
  gpsrUserRequest,
  brand,
  svidMerk,
  gpsrCategoryId,
  carId,
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isGpsrDialogOpen, setIsGpsrDialogOpen] = useState(false)
  const formikRef = useRef(null)
  const theme = useContext(ThemeContext)
  const isMobile = useMediaLayout({ maxWidth: theme.metrics.tablet - 1 })
  const [disabledSubmit, setDisabledSubmit] = useState(true)

  /**
   * Insert a new GPSR party data
   */
  const [insertParty] = useMutation(INSERT_PARTY)

  const statusIcon = {
    inactive: 'publish',
    error: 'publishedError',
    ok: 'published',
    update: 'publishedWait',
  }[status]
  const statusColor = {
    inactive: 'brandAlfa',
    error: 'stateNegative',
    ok: 'statePositive',
  }[status]

  // Show GPSR link only if gpsrUserRequest is true and portal is Marktplaats
  const shouldShowGpsrLink =
    gpsrUserRequest && title === GPSR_PORTAL_NAMES.MARKTPLAATS

  const handleGpsrLinkClick = (e) => {
    e.preventDefault()
    setIsGpsrDialogOpen(true)
  }

  const handleCloseGpsrDialog = () => {
    setIsGpsrDialogOpen(false)
  }

  const handleGpsrFormSubmit = async (values) => {
    try {
      await insertParty({
        variables: {
          brand,
          svid_merk: Number(svidMerk),
          category_id: Number(gpsrCategoryId),
          carfileId: Number(carId),
          gpsr_message_off: false,
          data: {
            type: values.type,
            name: values.manufacturerName,
            address: values.address,
            postal_code: values.postalCode,
            city: values.place,
            province: values.province,
            phone_number: values.phoneNumber,
            website: values.website,
            email_address: values.email,
          },
        },
      })

      // After updating the GPSR party data, refresh the carfile data
      if (carId) {
        dispatch(getCarfile(carId))
      }

      setIsGpsrDialogOpen(false)
    } catch (err) {
      console.error('Error submitting GPSR form:', err)
    }
  }

  return (
    <>
      <ExpandableRow
        main={
          <>
            <Checkbox
              disabled={disabled}
              checked={selected}
              onChange={onChange}
              name={title}
              data-test-e2e={formatDataTestE2eAttr('portal', title)}
            />
            <Text type="tabSmall" text={title} />
            {!isMobile && shouldShowGpsrLink && (
              <Button
                type="button"
                level="option"
                text={t('gpsrForm.missingInfo')}
                onClick={handleGpsrLinkClick}
              />
            )}
          </>
        }
        details={
          status && [
            <React.Fragment key="icon-with-date">
              <Icon color="brandCharlie" size="md" type="create" />
              <Date color="brandCharlie" text={date} />
            </React.Fragment>,
            <React.Fragment key="icon-without-date">
              <Icon color={statusColor} type={statusIcon} />
            </React.Fragment>,
          ]
        }
      >
        <>
          {/* Mobile: button inside body before log */}
          {isMobile && shouldShowGpsrLink && (
            <StyledGpsrButton
              type="button"
              level="option"
              text={t('gpsrForm.missingInfo')}
              onClick={handleGpsrLinkClick}
              style={{ marginBottom: '8px' }}
            />
          )}
          <Text
            color={statusColor}
            type="body"
            dangerouslySetInnerHTML={{ __html: log }}
          />
        </>
      </ExpandableRow>

      <FlexibleDialog
        open={isGpsrDialogOpen}
        closeHandler={handleCloseGpsrDialog}
        title={t('gpsrForm.addGpsrDetails')}
        content={
          <GpsrDialogForm
            ref={formikRef}
            onSubmit={handleGpsrFormSubmit}
            validationSchema={GpsrPartyDataDialogValidationSchema(t)}
            onFormState={({ isValid }) => setDisabledSubmit(!isValid)}
          />
        }
        cancel
        cancelText={t('cancel')}
        onCancel={handleCloseGpsrDialog}
        submitText={t('save')}
        onSubmit={() => formikRef.current?.submitForm()}
        scrollableContent
        disabledSubmit={disabledSubmit}
      />
    </>
  )
}

Portal.propTypes = {
  date: PropTypes.string.isRequired,
  log: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  status: PropTypes.string,
  title: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  gpsrUserRequest: PropTypes.object,
  brand: PropTypes.string,
  svidMerk: PropTypes.number,
  gpsrCategoryId: PropTypes.number,
  carId: PropTypes.string,
}

Portal.defaultProps = {
  log: null,
  status: null,
  selected: false,
  brand: null,
  svidMerk: null,
  gpsrCategoryId: null,
  carId: null,
}

export default withApolloV4Provider(Portal)
